'use client';
import { routes } from '@/routes';
import { useAppStore } from '@/stores/appStore';
import { getFundsDetails } from '@/utils';
import { signout } from '@/utils/auth';
import type { AppContextProps } from '@/wrappers/AppContext';
import { AppContext } from '@/wrappers/AppContext';
import { AppHeader, HeaderLayout } from '@wolfejs/ms-ui/Headers';
import { UI } from '@wolfejs/ui/UI';
import { useUIStore } from '@wolfejs/ui/stores/UIStore';
import { useParams, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { appHeaderNavItems, popoverUserMenuItems } from './navItems';
import type { UserEntity } from '@wolfejs/ms-api-js/src/types/users';
import { usePathname } from 'next/navigation';

const AppHeaderContainer = () => {
  const router = useRouter();
  const { portalSlug: slug } = useParams();
  const user = useAppStore(state => state.user);
  const themeMode = useUIStore(state => state.themeMode);
  const availableFunds = useAppStore(state => state.availableFunds);
  const setAvailableFunds = useAppStore(state => state.setAvailableFunds);
  const pathname = usePathname();

  const fetchFunds = async () => {
    // Commenting out availableFunds cache for the time being:
    // if (availableFunds) return;
    const response = await getFundsDetails();
    if (!response) {
      setAvailableFunds(undefined);
      return;
    }
    if (!response || typeof response === 'string') return;
    setAvailableFunds(response.amount ?? 0);
  };

  const [displayedUser, setDisplayedUser] = useState<UserEntity|undefined>(undefined);
  const [displayedFunds, setDisplayedFunds] = useState(0);

  useEffect(() => {
    // This check ensures that we only update displayedFunds when availableFunds is defined
    if (user !== undefined) {
      setDisplayedUser(user);
    }
  }, [user]); // Dependency on availableFunds means this effect runs whenever availableFunds changes

  useEffect(() => {
    //Refetch funds on page change or component refresh.
    fetchFunds();
  }, [pathname]);

  useEffect(() => {
    // This check ensures that we only update displayedFunds when availableFunds is defined.
    // This fixes a mismatch between the markup generated between the
    // server side (which does not have access to the state) and the client side.
    if (availableFunds !== undefined) {
      setDisplayedFunds(availableFunds);
    }
  }, [availableFunds]); // Dependency on availableFunds means this effect runs whenever availableFunds changes

  let appContext: AppContextProps;

  async function handleSignoutClick() {
    if (await signout()) {
      appContext.setAccessToken?.(undefined);

      router.push(`/${slug}/${routes.signin}`);
      return;
    }

    UI.notifications.error('Failed to sign out.');
  }

  return (
    <AppContext.Consumer>
      {context => {
        appContext = context;

        const brandlogo = context.portal?.logomark ? (
          <img
            className="h-[32px]"
            src={
              themeMode === 'dark' && context.portal?.logomarkReverse
                ? '/wolfe/logomark-reverse.svg'
                : '/wolfe/logomark.svg'
            }
            alt={context.portal?.name}
          />
        ) : (
          <img
            className="h-[32px]"
            src={themeMode === 'dark' ? '/wolfe/logomark-reverse.svg' : '/wolfe/logomark.svg'}
            alt={context.portal?.name}
          />
          // <div className="flex h-[32px] w-[32px] items-center justify-center bg-default-200 text-2xl dark:bg-default-800">
          //   {context.portal?.name}
          // </div>
        );

        return (
          <AppHeader
            onSignOutClick={handleSignoutClick}
            brandLogo={brandlogo}
            menuOptions={appHeaderNavItems}
            popoverUserMenuItems={[
              {
                text: user?.name,
                helperText: user?.email,
                id: 'userData',
              },
              ...popoverUserMenuItems,
            ]}
            layout={HeaderLayout.Minimal}
            routes={routes}
            user={displayedUser}
            slug={slug}
            availableFunds={displayedFunds as number}
            showShoppingCart={true}
          />
        );
      }}
    </AppContext.Consumer>
  );
};

export default AppHeaderContainer;
